import * as THREE from "three";
import { PLACE_BOUND_XY, PLACE_BOUND_Z } from "./constants";

export const EDIT_SNAP_VALUES = ["0.1", "0.01", "0.001"] as const;
export type EditSnap = (typeof EDIT_SNAP_VALUES)[number];

/**
 * World-space grid spacing for the EDIT snap setting. The string enum doubles as
 * the metre value so a parse keeps the two in sync.
 */
export function snapGrid(snap: EditSnap): number {
  return Number(snap);
}

export function quantize(value: number, grid: number): number {
  const decimals = Number.isInteger(grid) ? 0 : (String(grid).split(".")[1]?.length ?? 0);
  const rounded = Math.round(value / grid) * grid;
  return Number(rounded.toFixed(decimals));
}

/**
 * Snaps a world position to the grid. In 2D the depth axis is left untouched
 * (the dimension lock owns z), in 3D all axes snap.
 */
export function snapPos(p: THREE.Vector3, grid: number, is2D: boolean): THREE.Vector3 {
  return new THREE.Vector3(
    quantize(p.x, grid),
    quantize(p.y, grid),
    is2D ? p.z : quantize(p.z, grid),
  );
}

/**
 * Clamps a world placement coordinate into the playable box (the diorama bounds),
 * in place. x/y are held to ±PLACE_BOUND_XY; z is held to ±PLACE_BOUND_Z only in
 * 3D, since 2D pins z to the dimension-lock plane. Every placement path funnels
 * through here so no entity can land beyond the diorama no matter the entry point.
 */
export function clampPlace(p: THREE.Vector3, is2D: boolean): void {
  p.x = THREE.MathUtils.clamp(p.x, -PLACE_BOUND_XY, PLACE_BOUND_XY);
  p.y = THREE.MathUtils.clamp(p.y, -PLACE_BOUND_XY, PLACE_BOUND_XY);
  if (!is2D) {
    p.z = THREE.MathUtils.clamp(p.z, -PLACE_BOUND_Z, PLACE_BOUND_Z);
  }
}
